/**
 * RepBuddy - Meter catalog (single frontend source of truth for spend + plan limits)
 *
 * One place that defines the metered resources, their per-plan allowances, which action
 * debits which meter, and the exact wording used everywhere spend is shown. The pricing
 * page, the account page, and the in-product cost messages all read from here so they can
 * never again disagree about what a plan includes.
 *
 * These allowances mirror the ENFORCED source of truth on the backend
 * (creditsService.PLAN_ALLOWANCE + usageService.PLAN_METRIC_ALLOWANCES). The account page
 * still shows each user's *live* balance from /api/credits (backed by getAllMeters); this
 * module supplies the labels, the plan headline numbers, and the copy. If the backend
 * allowances ever change, update them here too - these two lists are meant to match.
 *
 * Meter <-> server mapping:
 *   leads   -> user_credits            (consume_credits)   1 per net-new verified lead
 *   verify  -> email_verifications     (consume_usage)     1 per address verified
 *   enrich  -> company_enrichments     (consume_usage)     1 per company row enriched
 *   website -> website_lookups         (consume_usage)     1 per website resolved
 *   ai      -> (never metered)                             drafting + chat are free
 */

// Canonical, ordered list of everything a plan meters (plus the never-metered AI row). The
// `metric` field is the server usage_meters key (null for leads, which live in user_credits,
// and for ai, which is never metered). `noun` is the singular unit used in cost copy.
export const METERS = [
  { key: 'leads',   metric: null,                  label: 'Lead credits',        noun: 'lead credit',        icon: 'user',     solo: 150,         pro: 500,          metered: true  },
  { key: 'verify',  metric: 'email_verifications', label: 'Email verifications', noun: 'email verification', icon: 'mail',     solo: 1000,        pro: 5000,         metered: true  },
  { key: 'enrich',  metric: 'company_enrichments', label: 'Company enrichments', noun: 'company enrichment', icon: 'building', solo: 500,         pro: 2500,         metered: true  },
  { key: 'website', metric: 'website_lookups',     label: 'Website lookups',     noun: 'website lookup',     icon: 'monitor',  solo: 250,         pro: 1250,         metered: true  },
  { key: 'ai',      metric: null,                  label: 'AI drafting & chat',  noun: null,                 icon: 'fileText', solo: 'Unlimited', pro: 'Unlimited',  metered: false },
];

export const METER_BY_KEY = Object.fromEntries(METERS.map((m) => [m.key, m]));
export const METER_BY_METRIC = Object.fromEntries(METERS.filter((m) => m.metric).map((m) => [m.metric, m]));

// Maps a product action onto the meter it debits, plus the noun-phrase that opens the
// "before" estimate ("This search could use up to..."). `hunt` debits lead credits;
// `enrich`/`verify`/`resolve` debit their usage meter; `draft`/`chat` are never metered.
export const ACTIONS = {
  hunt:    { meterKey: 'leads',   subject: 'This search' },
  enrich:  { meterKey: 'enrich',  subject: 'This enrichment' },
  verify:  { meterKey: 'verify',  subject: 'This verification' },
  resolve: { meterKey: 'website', subject: 'This lookup' },
  draft:   { meterKey: 'ai' },
  chat:    { meterKey: 'ai' },
};

// ─── Plan catalogue (the numbers pricing + account both render) ────────────────
export const PLAN_KEYS = ['Solo', 'Pro'];

/** The allowance for one meter on one plan ('Solo' | 'Pro'). Strings like 'Unlimited' pass through. */
export function allowanceFor(meterKey, plan) {
  const m = METER_BY_KEY[meterKey];
  if (!m) return null;
  return plan === 'Pro' ? m.pro : m.solo;
}

/**
 * The per-plan usage rows the Account page + plan picker render. One row per meter, in
 * catalogue order, carrying the label/icon/unit and the plan's numeric allowance (or the
 * 'Unlimited' string for AI). This is the shape billingService.PLAN_LIMITS used to hardcode.
 */
export function planRows(plan) {
  return METERS.map((m) => {
    const limit = plan === 'Pro' ? m.pro : m.solo;
    return { key: m.key, label: m.label, icon: m.icon, unit: typeof limit === 'number' ? '/ mo' : '', limit };
  });
}

// ─── Formatting helpers ────────────────────────────────────────────────────────
/** Thousands-separated number, or passthrough for strings like "Unlimited". */
export function formatNumber(n) {
  return typeof n === 'number' ? n.toLocaleString('en-US') : n;
}

/**
 * Pluralize a unit noun for a count. Handles the endings that actually occur in our meter
 * and reconciliation nouns: pluralize('lead credit', 3) -> 'lead credits',
 * pluralize('company', 2) -> 'companies', pluralize('address', 2) -> 'addresses'.
 */
export function pluralize(noun, count) {
  if (!noun) return '';
  if (count === 1) return noun;
  if (/[^aeiou]y$/i.test(noun)) return noun.replace(/y$/i, 'ies');
  if (/(s|x|z|ch|sh)$/i.test(noun)) return `${noun}es`;
  return `${noun}s`;
}

/** Copy for actions that never touch a meter (AI drafting, chat). */
export function neverMeteredLine() {
  return "This won't use any credits - AI drafting and chat are included free on every plan.";
}

/**
 * BEFORE an action runs: a worst-case estimate against the correct meter + the balance.
 *   estimateLine('hunt',   { upTo: 10, remaining: 127, allowance: 150 })
 *     -> "This search could use up to 10 lead credits. You have 127 of 150 remaining."
 *   estimateLine('enrich', { upTo: 40, remaining: 460, allowance: 500 })
 *     -> "This enrichment could use up to 40 company enrichments. You have 460 of 500 remaining."
 * Returns null when there's nothing meaningful to show (no positive estimate, or the user
 * is unmetered so no balance is known).
 */
export function estimateLine(action, { upTo, remaining, allowance } = {}) {
  const spec = ACTIONS[action];
  if (!spec) return null;
  const meter = METER_BY_KEY[spec.meterKey];
  if (!meter) return null;
  if (!meter.metered) return neverMeteredLine();
  if (!(upTo > 0)) return null;
  let line = `${spec.subject} could use up to ${formatNumber(upTo)} ${pluralize(meter.noun, upTo)}.`;
  if (typeof remaining === 'number' && typeof allowance === 'number') {
    line += ` You have ${formatNumber(remaining)} of ${formatNumber(allowance)} remaining.`;
  }
  return line;
}

/**
 * AFTER an action completes: the actual charge, an optional "not charged" reconciliation,
 * and the new balance - so the before-estimate and the after-actual visibly reconcile.
 *   actualLine('hunt', { used: 7, notCharged: 3,
 *                        notChargedNoun: 'contact', notChargedReason: 'had no verified email',
 *                        remaining: 120, allowance: 150 })
 *     -> "Used 7 lead credits. 3 contacts had no verified email and were not charged.
 *         You have 120 of 150 remaining."
 * `remaining`/`allowance` are omitted from the sentence when unknown (e.g. unmetered dev
 * accounts) rather than shown as blanks.
 */
export function actualLine(action, {
  used = 0, remaining, allowance,
  notCharged = 0, notChargedNoun = 'item', notChargedReason,
  // A row can now be free for more than one reason, so the single scalar pair above is no longer
  // enough: a contact can be skipped for having no address, OR because the user already owns it.
  //
  // `[{ count, clause }]`, where each entry is a COMPLETE sentence after its number - not a
  // fragment to be glued together. Stitching fragments produced things like "3 contacts you
  // already own - were not charged", which is the sound of a template rather than a sentence.
  // Each reason knows how to say itself; this only counts and orders them.
  notChargedParts = null,
} = {}) {
  const spec = ACTIONS[action];
  if (!spec) return null;
  const meter = METER_BY_KEY[spec.meterKey];
  if (!meter) return null;
  if (!meter.metered) return neverMeteredLine();

  const parts = (notChargedParts && notChargedParts.length)
    ? notChargedParts.filter(p => p && p.count > 0 && p.clause)
    // The scalar form, kept because other callers still use it and it renders the same sentence
    // it always did.
    : (notCharged > 0 && notChargedReason
        ? [{
            count: notCharged,
            clause: `${pluralize(notChargedNoun, notCharged)} ${notChargedReason} and ` +
                    `${notCharged === 1 ? 'was' : 'were'} not charged`,
          }]
        : []);

  const freeTotal = parts.reduce((n, p) => n + p.count, 0);
  const why = parts.map(p => `${formatNumber(p.count)} ${p.clause}.`).join(' ');

  // Nothing was billed, and we can say why. "Used 0 lead credits" is true and reads like a
  // machine reporting a null; this is what a person would say, and being able to say it is the
  // entire point of the free re-pull.
  if (used === 0 && freeTotal > 0) {
    let line = `No ${pluralize(meter.noun, 0)} used — ${why.charAt(0).toLowerCase()}${why.slice(1)}`;
    if (typeof remaining === 'number' && typeof allowance === 'number') {
      line += ` You still have ${formatNumber(remaining)} of ${formatNumber(allowance)} remaining.`;
    }
    return line;
  }

  let line = `Used ${formatNumber(used)} ${pluralize(meter.noun, used)}.`;
  if (why) line += ` ${why}`;
  if (typeof remaining === 'number' && typeof allowance === 'number') {
    line += ` You have ${formatNumber(remaining)} of ${formatNumber(allowance)} remaining.`;
  }
  return line;
}
